'use client';

import type {Projet, DelegationInfo} from './TunisiaMap';

interface DelegationPanelProps {
  delegation: DelegationInfo;
  projets: Projet[];
  onClose: () => void;
  onSelectProjet: (projet: Projet) => void;
}

function statutBadge(statut: string) {
  const colors: Record<string, string> = {
    en_cours: 'bg-green-100 text-green-800',
    publie: 'bg-blue-100 text-blue-800',
    en_collecte: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800',
    termine: 'bg-gray-100 text-gray-600',
    cloture: 'bg-gray-100 text-gray-600',
  };
  return colors[statut] || 'bg-gray-100 text-gray-600';
}

export default function DelegationPanel({delegation, projets, onClose, onSelectProjet}: DelegationPanelProps) {
  // Filter projects: match delegation name first, fallback to gouvernorat
  const delegName = delegation.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const gouvName = delegation.gouvernorat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  let delegProjets = projets.filter(p => {
    const pd = (p.delegation || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return pd === delegName || pd.includes(delegName) || delegName.includes(pd);
  });
  // If no match by delegation, show all from gouvernorat
  if (delegProjets.length === 0) {
    delegProjets = projets.filter(p => {
      const pg = (p.gouvernorat || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return pg === gouvName || pg.includes(gouvName) || gouvName.includes(pg);
    });
  }

  return (
    <div className="w-full h-full bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-primary text-white p-4 flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-lg font-bold">{delegation.name}</h2>
          <p className="text-white/70 text-sm">{delegation.gouvernorat}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-white/20 transition-colors"
          aria-label="Fermer"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Projects count */}
      <div className="px-4 py-3 bg-surface border-b border-border shrink-0">
        <p className="text-sm text-muted">
          <span className="font-bold text-foreground">{delegProjets.length}</span> projet{delegProjets.length !== 1 ? 's' : ''} dans cette delegation
        </p>
      </div>

      {/* Projects list */}
      <div className="flex-1 overflow-y-auto">
        {delegProjets.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-muted text-sm">Aucun projet dans cette délégation pour le moment.</p>
            <p className="text-muted text-xs mt-2">Les projets apparaitront ici une fois publies sur la carte.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {delegProjets.map(projet => {
              const pct = projet.budget_objectif > 0
                ? Math.round((projet.montant_collecte / projet.budget_objectif) * 100)
                : 0;
              return (
                <button
                  key={projet.id}
                  onClick={() => onSelectProjet(projet)}
                  className="w-full text-start p-4 hover:bg-surface/80 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground line-clamp-2">{projet.titre}</h3>
                      <p className="text-xs text-muted mt-1">{projet.categorie}</p>
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${statutBadge(projet.statut)}`}>
                      {projet.statut.replace('_', ' ')}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted">{projet.montant_collecte.toLocaleString()} TND</span>
                      <span className="font-medium text-primary">{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-border rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{width: `${Math.min(pct, 100)}%`}}
                        role="progressbar"
                        aria-valuenow={pct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                    <p className="text-xs text-muted mt-1 text-end">Objectif: {projet.budget_objectif.toLocaleString()} TND</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-border bg-surface shrink-0 text-center">
        <a
          href={`/fr/projets`}
          className="text-xs text-primary hover:text-primary-light font-medium"
        >
          Voir tous les projets de {delegation.name} →
        </a>
      </div>
    </div>
  );
}
